import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';
import RegistrationStep1 from './registration/RegistrationStep1';
import RegistrationStep2 from './registration/RegistrationStep2';
import RegistrationStep3 from './registration/RegistrationStep3';
import RegistrationStep4 from './registration/RegistrationStep4';
import SuccessModal from './common/SuccessModel';
import RoomAllocationScreen from './Roomallocationscreen ';

const FeatureItem = ({ text, delay }) => (
  <div className="feature-item" style={{ animationDelay: delay }}>
    <div className="feature-icon"></div>
    <span className="feature-text">{text}</span>
  </div>
);

const LeftPanel = ({ subtitle, features }) => (
  <div className="login-left">
    <div className="login-branding">
      <div className="logo-lockup">
        <div className="logo-text">OMKARA</div>
        <div className="logo-divider"></div>
        <div className="logo-tagline">Hostel Management</div>
      </div>
      <p className="login-welcome">{subtitle}</p>
      <div className="login-features">
        {features.map((f, i) => <FeatureItem key={i} text={f} delay={`${0.2 + i * 0.2}s`} />)}
      </div>
    </div>
  </div>
);

function useTimer(seconds = 60) {
  const [timer, setTimer] = useState(0);
  const start = () => setTimer(seconds);
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);
  return [timer, start];
}

const OtpRow = ({ values, setValues, inputRefs }) => {
  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...values];
    next[i] = v;
    setValues(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !values[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };
  return (
    <div style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
      {values.map((v, i) => (
        <input
          key={i}
          ref={el => inputRefs.current[i] = el}
          type="tel" maxLength={1} value={v}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          style={{
            width: 46, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 700,
            border: v ? '2px solid #1B3B5F' : '2px solid #E8E4DF',
            borderRadius: 8, outline: 'none', fontFamily: "'Work Sans', sans-serif",
            background: v ? '#F0F4F8' : 'white', color: '#1B3B5F',
            transition: 'border-color 0.2s',
          }}
        />
      ))}
    </div>
  );
};

const S = {
  errBox:    { background: 'rgba(232,93,74,0.08)', borderLeft: '4px solid #E85D4A', padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#E85D4A', borderRadius: 4 },
  succBox:   { background: '#D4EDDA', borderLeft: '4px solid #2D5F4F', padding: '20px', borderRadius: 8, textAlign: 'center', marginBottom: 20 },
  inputWrap: { display: 'flex', alignItems: 'center', border: '2px solid #E8E4DF', borderRadius: 8, overflow: 'hidden' },
  inputPrefix: { padding: '14px', background: '#F8F6F3', color: '#6B6B6B', fontSize: 15, fontWeight: 600, borderRight: '1px solid #E8E4DF', whiteSpace: 'nowrap' },
  otpFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  otpTimer:  { fontSize: 13, color: '#6B6B6B' },
  resendBtn: (disabled) => ({ fontSize: 13, fontWeight: 600, color: disabled ? '#C4BEB8' : '#1B3B5F', background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer' }),
  backLink:  { fontSize: 13, fontWeight: 600, color: '#6B6B6B', background: 'none', border: 'none', cursor: 'pointer' },
};

const GuestRegistration = () => {
  const [screen,      setScreen]      = useState(() => {
    const token = localStorage.getItem('token');
    const role  = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}').role; } catch{ return ''; } })();
    if (token && role === 'STUDENT') {
      return localStorage.getItem('guestScreen') || 'registration1';
    }
    return 'otp';
  });
  const [navKey,      setNavKey]      = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const [mobile,   setMobile]   = useState('');
  const [isIntl,   setIsIntl]   = useState(false); // ✅ tracks international mode independently
  const [otpStep,  setOtpStep]  = useState('mobile');
  const [otp,      setOtp]      = useState(['','','','','','']);
  const [timer,    startTimer]  = useTimer(60);
  const otpRefs = useRef([]);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const sendOTP = async () => {
    setError('');
    // ✅ Allow Indian 10-digit or international (+countrycode)
    const strippedMobile = mobile.trim().replace(/\s/g, '');
    const isIndian = /^[6-9]\d{9}$/.test(strippedMobile);
    const isInternational = /^\+[1-9]\d{6,14}$/.test(strippedMobile);
    if (!isIndian && !isInternational) {
      setError('Enter valid 10-digit Indian number or international number with country code (e.g. +44 20 7946 0958)');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/send-otp', { mobile: mobile.trim().replace(/\s/g, '') });
      setOtpStep('otp');
      setOtp(['','','','','','']);
      startTimer();
    } catch (err) {
      const isDuplicate = err.response?.data?.duplicate === true;
      if (isDuplicate) {
        setError('This mobile number is already registered. Please contact hostel admin.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to send OTP. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    setError('');
    const code = otp.join('');
    if (code.length < 6) { setError('Please enter the complete 6-digit OTP.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login-otp', { mobile: mobile.trim().replace(/\s/g, ''), otp: code });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify({
          id:        res.data.id,
          username:  res.data.username,
          role:      res.data.role,
          firstName: res.data.firstName,
          lastName:  res.data.lastName,
        }));
        // ✅ Save BEFORE setTimeout — prevents mobile not auto-filling in registration form
        const cleanMobile = mobile.trim().replace(/\s/g, '');
        localStorage.setItem('guestMobile', cleanMobile);
        sessionStorage.setItem('guestMobile', cleanMobile);
        setOtpStep('success');
        setTimeout(() => navigateTo('registration1'), 1200);
      } else {
        setError('Invalid or expired OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const resetOtp = () => {
    setOtpStep('mobile'); setMobile(''); setIsIntl(false);
    setOtp(['','','','','','']); setError('');
  };

  const navigateTo = useCallback((sc, params = {}) => {
    if (sc === 'registration1' && !params.isBack) {
      localStorage.removeItem('registrationStep1Data');
      localStorage.removeItem('registrationStep2Data');
      localStorage.removeItem('registrationStep3Data');
      localStorage.removeItem('registrationPhotoPreview');
      window.selectedPhotoFile = null;
      window.selectedDocFiles  = null;
      sessionStorage.removeItem('regPhotoData');
    }
    setScreen(sc);
    setNavKey(k => k + 1);
    if (sc !== 'otp') {
      localStorage.setItem('guestScreen', sc);
    } else {
      localStorage.removeItem('guestScreen');
    }
    window.scrollTo(0, 0);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('guestScreen');
    localStorage.removeItem('guestMobile');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setScreen('otp');
    resetOtp();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setScreen('rooms');
  };

  if (screen === 'otp') {
    return (
      <div className="login-container">
        <LeftPanel
          subtitle="Start your admission journey. Verify your mobile number and complete the registration form — takes less than 10 minutes."
          features={[
            'Fill once — admin reviews and confirms',
            'Upload KYC documents digitally',
            'Track your application status in real time',
          ]}
        />

        <div className="login-right">
          <div className="login-card">
            <div className="login-header">
              <h2>Guest Registration</h2>
              <p>Verify your mobile number to begin</p>
            </div>

            {error && <div style={S.errBox}>⚠️ {error}</div>}

            {otpStep === 'success' && (
              <div style={S.succBox}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
                <div style={{ fontWeight: 700, color: '#155724', fontSize: 15 }}>
                  Mobile verified! Opening your registration form…
                </div>
              </div>
            )}

            {otpStep === 'mobile' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Your Mobile Number</label>
                  <div style={S.inputWrap}>
                    {/* ✅ Dynamic prefix — +91 for Indian, 🌐 for international */}
                    <span style={S.inputPrefix}>
                      {isIntl ? '🌐' : '+91'}
                    </span>
                    <input
                      type="tel" className="form-input has-prefix"
                      placeholder={isIntl ? '+44 20 7946 0958' : '98765 43210'}
                      maxLength={18}
                      value={mobile}
                      onChange={e => {
                        let val = e.target.value;
                        if (isIntl) {
                          // International mode — allow + digits spaces
                          if (!val.startsWith('+')) val = '+' + val.replace(/[^0-9 ]/g, '');
                          else val = '+' + val.slice(1).replace(/[^0-9 ]/g, '');
                        } else {
                          // Indian mode — digits only
                          val = val.replace(/\D/g, '');
                        }
                        setMobile(val);
                      }}
                      onKeyDown={e => e.key === 'Enter' && sendOTP()}
                      style={{ border: 'none', outline: 'none', flex: 1, padding: '14px 12px' }}
                    />
                    <span style={{ padding:'0 12px', display:'flex', alignItems:'center' }}>
                      <svg width="24" height="24" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="16" fill="#25D366"/>
                        <path d="M23.5 8.5A10.4 10.4 0 0 0 16 5.5C10.2 5.5 5.5 10.2 5.5 16c0 1.8.5 3.6 1.4 5.2L5.5 26.5l5.4-1.4A10.4 10.4 0 0 0 16 26.5c5.8 0 10.5-4.7 10.5-10.5 0-2.8-1.1-5.4-3-7.5zm-7.5 16.1a8.6 8.6 0 0 1-4.4-1.2l-.3-.2-3.2.8.8-3.1-.2-.3A8.6 8.6 0 1 1 16 24.6zm4.7-6.4c-.3-.1-1.6-.8-1.8-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.6-1.9-.2-.3 0-.4.1-.6l.4-.5c.1-.2.2-.3.2-.5 0-.2 0-.3-.1-.5-.1-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.2.2 2 3 4.8 4.2.7.3 1.2.4 1.6.5.7.2 1.3.2 1.8.1.6-.1 1.6-.6 1.9-1.2.3-.6.3-1.1.2-1.2-.1-.1-.3-.2-.6-.3z" fill="white"/>
                      </svg>
                    </span>
                  </div>
                  {/* ✅ Toggle button */}
                  <div style={{ marginTop: 8 }}>
                    {!isIntl ? (
                      <span
                        onClick={() => { setIsIntl(true); setMobile('+'); setError(''); }}
                        style={{ fontSize: 12, color: '#1B3B5F', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}>
                        🌐 Using international number? Tap here
                      </span>
                    ) : (
                      <span
                        onClick={() => { setIsIntl(false); setMobile(''); setError(''); }}
                        style={{ fontSize: 12, color: '#1B3B5F', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}>
                        🇮🇳 Switch back to Indian number
                      </span>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 0, lineHeight: 1.6 }}>
                  This number will be your login credential and the primary contact for all hostel communications.
                </p>
                <button className="btn-primary btn-gold" onClick={sendOTP} disabled={loading} style={{ marginTop: 28 }}>
                  {loading ? 'Sending OTP…' : 'Send OTP'}
                </button>

                {/* ✅ Help contact — shown below Send OTP button */}
                <div style={{
                  marginTop: 20,
                  padding: '12px 16px',
                  background: '#F4F1ED',
                  borderRadius: 8,
                  borderLeft: '3px solid #D4AF37',
                  fontSize: 13,
                  color: '#6B6B6B',
                  lineHeight: 1.7,
                }}>
                  <span style={{ fontWeight: 700, color: '#1B3B5F' }}>Need help?</span>
                  <br />
                  If you face any issue during registration or login, please contact us:
                  <br />
                  <a href="tel:+919925163221" style={{
                    color: '#1B3B5F',
                    fontWeight: 700,
                    textDecoration: 'none',
                    fontSize: 14,
                  }}>
                    📞 +91 99251 63221
                  </a>
                </div>
              </div>
            )}

            {otpStep === 'otp' && (
              <div>
                <div className="form-group">
                  <label className="form-label">OTP sent to {isIntl ? mobile : `+91 ${mobile.slice(0,5)} ${mobile.slice(5)}`}</label>
                  <OtpRow values={otp} setValues={setOtp} inputRefs={otpRefs} />
                  <div style={S.otpFooter}>
                    {timer > 0
                      ? <span style={S.otpTimer}>Resend in <strong style={{ color: '#1B3B5F' }}>{timer}s</strong></span>
                      : <span />}
                    <button style={S.resendBtn(timer > 0)} disabled={timer > 0} onClick={sendOTP}>Resend OTP</button>
                  </div>
                </div>
                <button className="btn-primary btn-gold" onClick={verifyOTP} disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify & Open Registration Form'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button style={S.backLink} onClick={resetOtp}>← Change number</button>
                </div>

                {/* ✅ Help contact — shown on OTP step too */}
                <div style={{
                  marginTop: 20,
                  padding: '12px 16px',
                  background: '#F4F1ED',
                  borderRadius: 8,
                  borderLeft: '3px solid #D4AF37',
                  fontSize: 13,
                  color: '#6B6B6B',
                  lineHeight: 1.7,
                }}>
                  <span style={{ fontWeight: 700, color: '#1B3B5F' }}>Need help?</span>
                  <br />
                  If you face any issue during registration or login, please contact us:
                  <br />
                  <a href="tel:+919925163221" style={{
                    color: '#1B3B5F',
                    fontWeight: 700,
                    textDecoration: 'none',
                    fontSize: 14,
                  }}>
                    📞 +91 99251 63221
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {screen === 'registration1' && <RegistrationStep1 key={`s1-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration2' && <RegistrationStep2 key={`s2-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration3' && <RegistrationStep3 key={`s3-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} />}
      {screen === 'registration4' && <RegistrationStep4 key={`s4-${navKey}`} onNavigate={navigateTo} onLogout={handleLogout} onSubmit={() => setShowSuccess(true)} />}
      {screen === 'rooms'         && <RoomAllocationScreen onNavigate={navigateTo} onLogout={handleLogout} />}
      {showSuccess && <SuccessModal onClose={handleSuccessClose} />}
    </div>
  );
};

export default GuestRegistration;